import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import ReactDOMServer from "react-dom/server";
import {
  Widget,
  addUserMessage,
  toggleMsgLoader,
  renderCustomComponent,
  deleteMessages,
} from "@falkor/react-chat-widget";
import parse from "html-react-parser";
import { customAlphabet } from "nanoid";
import moment from "moment";
import "@falkor/react-chat-widget/lib/styles.css";
import logo from "./logo.svg";
import { createParser } from "eventsource-parser";
import axios from "axios";

const alphabet = "0123456789abcdefghijklmnopqrstuvwxyz";
const nanoid = customAlphabet(alphabet, 10);

const API_URL = process.env.REACT_APP_API_URL;
const ENGINE_URL = process.env.REACT_APP_ENGINE_URL;
const SPEAKER = process.env.REACT_APP_SPEAKER;
const SPEAKER_MUTE = process.env.REACT_APP_SPEAKER_MUTE;
const BUDIBASE_TOKEN = process.env.REACT_APP_BUDIBASE_TOKEN;
const WEBCHAT_TOKEN = process.env.REACT_APP_WEBCHAT_TOKEN;
const CITIBOT_LANDING_PAGE_URL = process.env.REACT_APP_CITIBOT_LANDING_PAGE_URL;
const USER_AVATAR = process.env.REACT_APP_USER_AVATAR;
const TRANSLATION_URL = process.env.REACT_APP_TRANSLATION_URL;
const FOLLOW_UP_MESSAGE = "Is there anything else I can help you with?";
const ALLOWED_IMAGE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/heif",
  "image/gif",
  "image/jpg",
  "image/heic",
];
let phoneNumber = "";
let launcherImg = "";
const uuid = nanoid();
let messageId = "";
let isFirstMessage = true;
let message = "";
let links = [];
let messageButtonColor = "grey";
let messageTextColor = "black";
let followUpMessage = FOLLOW_UP_MESSAGE;
let userLanguage = "en";
let isAlreadyTranslated = false;
let isMessageButtonEnabled = false;
let isMessageButtonEnabledOnFailure = false;
let isMultiligualEnabled = false;
let isPoweredByHidden = false;
let isPhotoUpload = false;
let allowScoutFeedback = false;
let scoutWorkflowId = "";
let isInFollowUpContext = false;
let allowUTMLink = false;
let utmSource = "";
let hideLinkPreviewCard = false;

const languageMap = {
  af: "af-ZA",
  am: "am-ET",
  ar: "ar-SA",
  az: "az-AZ",
  bn: "bn-BD",
  bg: "bg-BG",
  ca: "ca-ES",
  cs: "cs-CZ",
  da: "da-DK",
  de: "de-DE",
  el: "el-GR",
  en: "en-US",
  et: "et-EE",
  fa: "fa-IR",
  fi: "fi-FI",
  fr: "fr-FR",
  gu: "gu-IN",
  he: "he-IL",
  hi: "hi-IN",
  hr: "hr-HR",
  hu: "hu-HU",
  id: "id-ID",
  is: "is-IS",
  it: "it-IT",
  ja: "ja-JP",
  ko: "ko-KR",
  lv: "lv-LV",
  lt: "lt-LT",
  ml: "ml-IN",
  mr: "mr-IN",
  mk: "mk-MK",
  ms: "ms-MY",
  ne: "ne-NP",
  nl: "nl-NL",
  no: "no-NO",
  pl: "pl-PL",
  pt: "pt-PT",
  ro: "ro-RO",
  ru: "ru-RU",
  si: "si-LK",
  sk: "sk-SK",
  sl: "sl-SI",
  es: "es-ES",
  sv: "sv-SE",
  ta: "ta-IN",
  te: "te-IN",
  th: "th-TH",
  tr: "tr-TR",
  uk: "uk-UA",
  ur: "ur-PK",
  vi: "vi-VN",
  zh: "zh-CN",
  zu: "zu-ZA",
};

// Reusable Hook for UTM Link updation
// This hook will add UTM parameters to the URL if allowUTMLink is true
const useUtmUrl = (url, allowUTMLink = false, utmSource = "default") => {
  return useMemo(() => {
    try {
      if (!allowUTMLink || !url) return url;
      const separator = url.includes("?") ? "&" : "?";
      const src = utmSource || "default";

      // Check if utmSource contains query string characters (& or =)
      // If yes, treat it as pre-formatted query parameters and append directly
      if (typeof src === "string" && /[&=]/.test(src)) {
        // Remove leading ? or & if present
        const cleanSrc = src.replace(/^[?&]+/, "");
        return `${url}${separator}utm_source=${cleanSrc}`;
      }

      // Otherwise, encode it as a simple utm_source value
      return `${url}${separator}utm_source=${encodeURIComponent(src)}`;
    } catch (e) {
      return url;
    }
  }, [url, allowUTMLink, utmSource]);
};

export const convertTextToHtml = (text) => {
  // Helper: escape attributes and text for safe insertion into HTML
  const escapeHtml = (str) =>
    (str || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");

  // Escape attribute values for inclusion in href/src/etc while preserving
  // raw ampersands when they are part of query strings. This prevents
  // double-encoding like `&amp;amp;` getting injected into the final URL.
  const escapeAttr = (str) =>
    (str || "")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");

  // Escape URL text for display (aria-label, link text) without escaping &
  // This prevents double-encoding when URLs contain query parameters
  const escapeUrlText = (str) =>
    (str || "")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");

  // Normalize href values by collapsing any already-escaped ampersand
  // sequences (e.g. &amp; or &amp;amp;) back to a single `&` so the
  // attribute contains a clean query separator.
  const normalizeHref = (s) => (s || "").replace(/&(amp;)+/gi, "&");

  // Helper: sanitize and normalize incoming URLs. Returns null for invalid/unlinkable values
  const sanitizeAndNormalizeUrl = (raw) => {
    if (!raw || typeof raw !== "string") return null;
    let u = raw.trim();
    // strip surrounding angle brackets or quotes
    u = u.replace(/^<|>$/g, "").replace(/^['"]+|['"]+$/g, "");
    // remove trailing punctuation that is commonly attached to URLs in text
    // Note: closing bracket ']' must be escaped inside the character class
    // For closing parentheses, only remove if they're unmatched (more closing than opening)
    const openParens = (u.match(/\(/g) || []).length;
    const closeParens = (u.match(/\)/g) || []).length;
    if (closeParens > openParens) {
      // Remove only the excess unmatched closing parentheses at the end
      const excess = closeParens - openParens;
      u = u.replace(new RegExp(`\\){${excess}}$`), "");
    }
    // Remove other trailing punctuation (but not matched parentheses)
    u = u.replace(/[\],;!?:.]+$/g, "");
    if (!u) return null;

    // If protocol-relative, add https:
    if (u.startsWith("//")) u = `https:${u}`;

    // If it already has http(s), verify it parses
    if (/^https?:\/\//i.test(u)) {
      try {
        // URL constructor will throw for invalid URLs
        return new URL(u).toString();
      } catch (e) {
        return null;
      }
    }

    // If it starts with "www." or looks like a domain (contains a dot and no spaces), add https://
    if (/^www\./i.test(u) || /^[^\s/]+\.[^\s/]+/i.test(u)) {
      try {
        return new URL(`https://${u}`).toString();
      } catch (e) {
        return null;
      }
    }

    // Otherwise, do not attempt to link (e.g., pure numeric ids)
    return null;
  };

  const appendUtmIfNeeded = (url) => {
    try {
      if (!allowUTMLink || !url) return url;
      const separator = url.includes("?") ? "&" : "?";
      const src = utmSource || "default";

      // Check if utmSource contains query string characters (& or =)
      // If yes, treat it as pre-formatted query parameters and append directly
      if (typeof src === "string" && /[&=]/.test(src)) {
        // Remove leading ? or & if present
        const cleanSrc = src.replace(/^[?&]+/, "");
        return `${url}${separator}utm_source=${cleanSrc}`;
      }

      // Otherwise, encode it as a simple utm_source value
      return `${url}${separator}utm_source=${encodeURIComponent(src)}`;
    } catch (e) {
      return url;
    }
  };

  const emailPattern = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g;
  // Match explicit http(s) URLs and protocol-less starting with www. or domain-like
  // Note: Allows parentheses in URLs as they are valid per RFC 3986
  const urlPattern = /(?:https?:\/\/|www\.)[^\s<>"']+/gi;
  // Also catch plain domains like example.com/path even if they don't start with www or http
  const domainLikePattern =
    /(?<!["'>])\b(?:[a-z0-9-]+\.)+[a-z]{2,}(?:\/[^\s<>"']*)?\b/gi;
  // Helper function to extract URL from markdown link with balanced parentheses
  // Standard regex can't handle nested parens, so we use a custom extraction
  const extractMarkdownLinkUrl = (text, startIndex) => {
    // startIndex should point to the opening ( after ]
    let depth = 0;
    let i = startIndex;
    while (i < text.length) {
      const ch = text[i];
      if (ch === "(") {
        depth++;
      } else if (ch === ")") {
        depth--;
        if (depth === 0) {
          // Found the matching closing paren
          return text.slice(startIndex + 1, i);
        }
      } else if (/\s/.test(ch) && depth === 1) {
        // Whitespace at depth 1 means end of URL (before optional title)
        return text.slice(startIndex + 1, i);
      }
      i++;
    }
    // No matching close paren found
    return null;
  };

  // Pattern to find potential markdown links: [text](
  const markdownLinkStartPattern = /\[([^\]]+)\]\(/g;
  // This is comes from the markdown syntax that is used in scout
  // eslint-disable-next-line no-useless-escape
  const phoneLinkPattern = /\[([^\]]+)\]\(tel:([0-9+\s\-\(\)]+)\)/g;
  const phonePattern =
    /(\+?\d{1,3}[-\s()]*)?(\(?\d{2,4}\)?[-\s]*){2,4}\d{2,4}/g; // detects numbers like (03) 5366 7100 or +91 98765 43210
  const boldPattern = /\*\*(.*?)\*\*/g; // **text** for bold
  const italicPattern = /\*(.*?)\*/g; // *text* for italic
  const headingPattern = /^(#{1,6})\s*(.*)$/gm; // Markdown-style headings
  const listItemPattern = /(?:^|\n)-\s+(.*)/g; // List items starting with `-`

  // First, handle explicit markdown-style links so they don't get double-wrapped
  // Use custom parsing to handle URLs with nested parentheses like (S(abc123))
  let htmlText = text || "";
  let mdMatch;
  const mdReplacements = [];
  while ((mdMatch = markdownLinkStartPattern.exec(htmlText)) !== null) {
    const linkText = mdMatch[1];
    const urlStartIndex = mdMatch.index + mdMatch[0].length - 1; // index of the opening (
    const url = extractMarkdownLinkUrl(htmlText, urlStartIndex);
    if (url !== null) {
      // Skip tel: links - they are handled by phoneLinkPattern later
      if (/^tel:/i.test(url)) {
        continue;
      }
      const fullMatchEnd = urlStartIndex + 1 + url.length + 1; // +1 for opening (, +1 for closing )
      const fullMatch = htmlText.slice(mdMatch.index, fullMatchEnd);
      const sanitized = sanitizeAndNormalizeUrl(url);
      let replacement;
      if (!sanitized) {
        replacement = escapeHtml(linkText);
      } else {
        const finalUrl = appendUtmIfNeeded(sanitized);
        const safeLabel = escapeHtml(`${linkText} (opens in a new tab)`);
        const hrefVal = escapeAttr(normalizeHref(finalUrl));
        replacement = `<a href="${hrefVal}" target="_blank" rel="noopener" aria-label="${safeLabel}">${escapeHtml(
          linkText
        )}</a>`;
      }
      mdReplacements.push({
        start: mdMatch.index,
        end: fullMatchEnd,
        replacement,
        fullMatch,
      });
    }
  }
  // Apply replacements in reverse order to preserve indices
  for (let i = mdReplacements.length - 1; i >= 0; i--) {
    const { start, end, replacement } = mdReplacements[i];
    htmlText = htmlText.slice(0, start) + replacement + htmlText.slice(end);
  }

  // Replace explicit http(s) and www links
  htmlText = htmlText.replace(urlPattern, (url, offset, fullText) => {
    // Skip replacing URLs already wrapped in an <a> tag
    const beforeOpen = fullText.lastIndexOf("<a", offset);
    const beforeClose = fullText.lastIndexOf("</a>", offset);
    if (beforeOpen > beforeClose) {
      return escapeHtml(url);
    }
    const sanitized = sanitizeAndNormalizeUrl(url);
    if (!sanitized) return escapeHtml(url);
    const finalUrl = appendUtmIfNeeded(sanitized);
    const label = escapeUrlText(`${url} (opens in a new tab)`);
    const hrefVal = escapeAttr(normalizeHref(finalUrl));
    return `<a href="${hrefVal}" target="_blank" rel="noopener" aria-label="${label}">${escapeUrlText(
      url
    )}</a>`;
  });

  // Catch domain-like patterns that were not matched above (avoid double-wrapping already linked text)
  htmlText = htmlText.replace(domainLikePattern, (match, offset, fullText) => {
    // skip if already inside an <a> tag or is an email (contains @)
    if (/@/.test(match)) return match;
    const beforeOpen = fullText.lastIndexOf("<a", offset);
    const beforeClose = fullText.lastIndexOf("</a>", offset);
    if (beforeOpen > beforeClose) {
      return escapeHtml(match);
    }
    const sanitized = sanitizeAndNormalizeUrl(match);
    if (!sanitized) return escapeHtml(match);
    const finalUrl = appendUtmIfNeeded(sanitized);
    const label = escapeUrlText(`${match} (opens in a new tab)`);
    const hrefVal = escapeAttr(normalizeHref(finalUrl));
    return `<a href="${hrefVal}" target="_blank" rel="noopener" aria-label="${label}">${escapeUrlText(
      match
    )}</a>`;
  });

  // Preserve explicit markdown-style tel links (e.g. [Call](tel:...))
  htmlText = htmlText.replace(
    phoneLinkPattern,
    (_match, phoneText, phoneNumber) => {
      const cleanNumber = phoneNumber.replace(/[^\d+]/g, "");
      return `<a href="tel:${escapeHtml(cleanNumber)}" aria-label="${escapeHtml(
        `Call ${phoneText}`
      )}">${escapeHtml(phoneText)}</a>`;
    }
  );

  // For phone numbers and emails, only replace inside text nodes so we
  // don't accidentally modify URLs or attributes that were already
  // converted to anchors above. Use DOM parsing to walk text nodes and
  // replace matches with anchor elements.
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlText, "text/html");
    const walker = doc.createTreeWalker(
      doc.body,
      NodeFilter.SHOW_TEXT,
      null,
      false
    );
    const textNodes = [];
    let prev = null;
    let tn = walker.nextNode();
    while (tn) {
      // Check if both the current node and the previous node are text nodes (Node.TEXT_NODE)
      if (prev && prev.nodeType === 3 && tn.nodeType === 3) {
        const prevText = prev.nodeValue || "";
        const currText = tn.nodeValue || "";

        const endsWithAtSign = /@$/.test(prevText);
        const startsWithAtSign = /^@/.test(currText);
        const continuesDomain =
          /@/.test(prevText) && /^[A-Za-z0-9-]+\./.test(currText);
        const continuesPlusTag =
          /[A-Za-z0-9._%+-]$/.test(prevText) && /^\+.*@/.test(currText);

        // merge if it looks like split parts of an email address
        const shouldMerge =
          endsWithAtSign || // e.g. "john@" + "example.com"
          startsWithAtSign || // e.g. "john.doe" + "@example.com"
          continuesDomain || // e.g. "john@" + "mail.example.com"
          continuesPlusTag; // e.g. "john.doe" + "+test@example.com"

        if (shouldMerge) {
          prev.nodeValue = prevText + currText;
          tn.nodeValue = "";
        } else {
          textNodes.push(tn);
          prev = tn;
        }
      } else {
        textNodes.push(tn);
        prev = tn;
      }
      tn = walker.nextNode();
    }

    textNodes.forEach((textNode) => {
      const original = textNode.nodeValue;
      let replaced = original;
      replaced = replaced.replace(emailPattern, (email) => {
        return `<a href="mailto:${escapeHtml(email)}">${escapeHtml(email)}</a>`;
      });

      // Safer phone number regex - fixes cross-line tel: linking issue (CIT-24893)
      const SEP = `[ \\t\\.\\-\\(\\)]`; // separators (no newline)
      const safePhonePattern = new RegExp(
        `(?:\\+?\\d{1,3}${SEP}*)?` + // optional country code
          `(?:\\(?\\d{1,4}\\)?${SEP}*)?` + // optional area code
          `(?:\\d{1,4}${SEP}*){1,3}\\d{1,4}`, // main number groups
        "g"
      );

      replaced = replaced.replace(safePhonePattern, (match) => {
        const cleanNumber = match.replace(/[^\d+]/g, "");
        const digitsOnly = cleanNumber.replace(/\D/g, "");
        if (digitsOnly.length < 7 || digitsOnly.length > 15) return match;
        return `<a href="tel:${escapeHtml(
          cleanNumber
        )}" aria-label="${escapeHtml(`Call ${match}`)}">${escapeHtml(
          match
        )}</a>`;
      });

      if (replaced !== original) {
        const range = doc.createRange();
        range.selectNode(textNode);
        const frag = range.createContextualFragment(replaced);
        textNode.parentNode.replaceChild(frag, textNode);
      }
    });

    htmlText = doc.body.innerHTML;

    // Fix: When innerHTML serializes the DOM, it encodes & as &amp; in href attributes.
    // We need to decode &amp; back to & in href/src attributes so URLs work correctly.
    // This regex matches href="..." or src="..." (with double quotes) and decodes &amp; within the quotes.
    htmlText = htmlText.replace(
      /\b(href|src)="([^"]*)"/g,
      (_match, attr, value) => {
        const decodedValue = value.replace(/&amp;/g, "&");
        return `${attr}="${decodedValue}"`;
      }
    );

    // Also handle single quotes (less common but possible)
    htmlText = htmlText.replace(
      /\b(href|src)='([^']*)'/g,
      (_match, attr, value) => {
        const decodedValue = value.replace(/&amp;/g, "&");
        return `${attr}='${decodedValue}'`;
      }
    );

    // Also decode &amp; in aria-label attributes (for URLs with query parameters)
    htmlText = htmlText.replace(/\baria-label="([^"]*)"/g, (_match, value) => {
      const decodedValue = value.replace(/&amp;/g, "&");
      return `aria-label="${decodedValue}"`;
    });

    // Decode &amp; in the text content of anchor tags (for displayed URLs)
    htmlText = htmlText.replace(
      /<a\s+([^>]*)>([^<]*)<\/a>/g,
      (_match, attrs, text) => {
        const decodedText = text.replace(/&amp;/g, "&");
        return `<a ${attrs}>${decodedText}</a>`;
      }
    );
  } catch (e) {
    // Fallback: if DOMParser isn't available (very unlikely in browser),
    // do the global replacements (less safe).
    htmlText = htmlText.replace(phonePattern, (match) => {
      const cleanNumber = match.replace(/[^\d+]/g, "");
      return `<a href="tel:${escapeHtml(cleanNumber)}" aria-label="${escapeHtml(
        `Call ${match}`
      )}">${escapeHtml(match)}</a>`;
    });
    htmlText = htmlText.replace(emailPattern, (email) => {
      return `<a href="mailto:${escapeHtml(email)}">${escapeHtml(email)}</a>`;
    });
  }

  htmlText = htmlText.replace(boldPattern, "<strong>$1</strong>");

  htmlText = htmlText.replace(italicPattern, "<em>$1</em>");

  htmlText = htmlText.replace(headingPattern, (_match, hashes, content) => {
    const level = hashes.length;
    return `<h${level}>${escapeHtml(content)}</h${level}>`;
  });

  htmlText = htmlText.replace(listItemPattern, "<li>$1</li>");

  // Wrap list items in <ul> if any <li> tags were found
  if (htmlText.includes("<li>")) {
    htmlText = htmlText.replace(/(?:<li>.*<\/li>)+/gs, (match) => {
      return `<ul>${match}</ul>`;
    });
  }

  return htmlText;
};

const focusOnInput = () => {
  setTimeout(() => {
    const inputElement = document.querySelector(
      '.rcw-input[contenteditable="true"]'
    );
    if (inputElement) {
      inputElement.focus();
      inputElement.setAttribute("aria-live", "polite");
      inputElement.setAttribute("aria-multiline", "true");
      inputElement.setAttribute("role", "textbox");
      inputElement.setAttribute("tabindex", "0");
    }
  }, 200);
};

const translateText = async (text, targetLanguageCode) => {
  if (targetLanguageCode === "en") return text;
  try {
    const response = await fetch(TRANSLATION_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ text, targetLanguageCode }),
    });
    const data = await response.json();
    return data.translatedText || text;
  } catch (error) {
    return text;
  }
};

const handleFollowUpMessageTranslation = (message) => {
  const translateRequestOptions = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      text: message,
      targetLanguageCode: "en",
    }),
  };

  fetch(`${TRANSLATION_URL}`, translateRequestOptions)
    .then((response) => {
      return response.json();
    })
    .then((data) => {
      userLanguage =
        data.sourceLanguageCode || data.error?.sourceLanguageCode || "en";

      if (userLanguage !== "en") {
        const followupTranslateRequestOptions = {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            text: followUpMessage,
            targetLanguageCode: userLanguage,
          }),
        };

        fetch(`${TRANSLATION_URL}`, followupTranslateRequestOptions)
          .then((response) => {
            return response.json();
          })
          .then((data) => {
            if (userLanguage !== "en") {
              followUpMessage = data.translatedText;
              isAlreadyTranslated = true;
            }
          })
          .catch((err) => {
            console.error("Translation Error: ", err);
          });
      } else {
        followUpMessage = FOLLOW_UP_MESSAGE;
        isAlreadyTranslated = true;
      }
    })
    .catch((err) => {
      console.error("Translation Error: ", err);
    });
};

const handleResponse = (response) => {
  return renderCustomComponent(
    () => (
      <CustomResponseMessageComponent
        key={response.messageId}
        message={response.message}
        links={response.links}
        buttons={response.options}
        messageId={response.messageId}
      />
    ),
    [],
    true
  );
};

function onParse(event, accumulatedMessages) {
  let parsedData = scoutWorkflowId ? JSON.parse(event.data) : undefined;

  if (event.type === "event") {
    if (
      (event.data === "[DONE]" && !scoutWorkflowId) ||
      (parsedData?.event_type === "workflow_run_completed" && scoutWorkflowId)
    ) {
      isFirstMessage = true;
      deleteMessages(1);
      handleResponse({
        message,
        links,
        messageId,
        options: [],
      });
      message = "";
      links = [];
      accumulatedMessages.length = 0;
      if (!allowScoutFeedback) {
        handleResponse({ message: followUpMessage });
        isInFollowUpContext = true;
        isAlreadyTranslated = false;
      }
    }
    if (event.data && event.data !== "[DONE]") {
      parsedData = JSON.parse(event.data);
      if (parsedData.message && parsedData.isFeedbackRequest) {
        // Backend already translates the feedback message and options
        // No need to translate again in the frontend
        handleResponse({
          message: parsedData.message,
          options: parsedData.options || [],
        });
      } else {
        if (scoutWorkflowId && parsedData.data) {
          // This is the block for streaming res for scout workflow
          if (parsedData.data.block_type === "com.scoutos.copilot.message") {
            if (parsedData.data.update_data?.output) {
              // Check if the output is different from the previous output
              if (
                accumulatedMessages.join("") !==
                parsedData.data.update_data.output
              ) {
                accumulatedMessages.push(parsedData.data.update_data.output);
              }
            }
            message = accumulatedMessages.join("");
          }
          // This is the block for sending links (Web pages) to the user
          if (
            (parsedData.data.block_type ===
              "com.scoutos.copilot_list_of_links" ||
              parsedData.data.block_type ===
                "com.scoutos.sandbox.javascript") &&
            parsedData.data.update_data?.output
          ) {
            links =
              parsedData.data.update_data.output?.links ??
              parsedData.data.update_data.output;
          }
          // Handle collections.v2.tables.query block type for search results
          // Check both state.output and update_data.output as the API can return either
          if (
            parsedData.data.block_type ===
              "com.scoutos.collections.v2.tables.query" &&
            (parsedData.data.state?.output ||
              parsedData.data.update_data?.output)
          ) {
            const outputData =
              parsedData.data.state?.output ||
              parsedData.data.update_data?.output;

            // Transform the output array to match the expected link format
            links = outputData.map((item) => ({
              title: item.record?.attributes?.page_title || "Unknown Title",
              url: item.record?.attributes?.url || "",
              metadata: {
                title: item.record?.attributes?.page_title || "Unknown Title",
                url: item.record?.attributes?.url || "",
              },
            }));
          }
        } else if (parsedData.output) {
          message = parsedData.output.output;
          links = parsedData.output.metadata?.sources;
        }
        if (message) {
          const output = {
            messageId,
            message,
            links: [],
            options: [],
          };
          if (isFirstMessage) {
            isFirstMessage = false;
            handleResponse(output);
          } else {
            const messageElement = document.getElementById(messageId);
            if (messageElement) {
              messageElement.innerHTML = output.message;
              messageElement.scrollIntoView({
                behavior: "smooth",
                block: "end",
              });
            }
          }
        }
      }
    }
  }
}

function setInputState(disabled) {
  const inputElement = document.querySelector(".rcw-input");

  if (inputElement) {
    if (disabled) {
      inputElement.setAttribute("contenteditable", "false");
      inputElement.setAttribute("aria-disabled", "true");
      inputElement.setAttribute("tabindex", "-1");
      inputElement.style.pointerEvents = "none";
      inputElement.style.opacity = "0.5";
      inputElement.blur();
    } else {
      inputElement.setAttribute("contenteditable", "true");
      inputElement.setAttribute("aria-disabled", "false");
      inputElement.setAttribute("tabindex", "0");
      inputElement.style.pointerEvents = "auto";
      inputElement.style.opacity = "1";
      inputElement.focus();
    }
  }

  setSendButtonEnabled(!disabled);
}

function setUIState(mode, enableDisableMessageButtonFlag) {
  // mode: "LOADING" | "OPTIONS" | "TEXT"

  if (mode === "LOADING") {
    toggleMsgLoader();
    setInputState(true);
    enableDisableMessageButton(false);
  }

  if (mode === "OPTIONS") {
    toggleMsgLoader();
    setInputState(true);
    enableDisableMessageButton(false);
  }

  if (mode === "TEXT") {
    toggleMsgLoader();
    setInputState(false);
    if (!enableDisableMessageButtonFlag) {
      enableDisableMessageButton(enableDisableMessageButtonFlag);
    } else {
      enableDisableMessageButton(true);
    }
    focusOnInput();
  }
}

// Engine Lex request
async function handleLexRequest(requestOptions) {
  try {
    const response = await fetch(`${ENGINE_URL}/web`, requestOptions);

    if (!response.ok) {
      const contentType = response.headers.get("Content-Type");
      const errorMessage =
        contentType && contentType.includes("application/json")
          ? (await response.json()).message ||
            `Something went wrong. Please try again later.`
          : `Something went wrong. Please try again later.`;
      if (isMessageButtonEnabledOnFailure) {
        const timer = setTimeout(() => {
          addMessageComponent(timer, true, "onBotError");
        }, 1000);
      }

      handleResponse({ message: errorMessage });
      setUIState("TEXT");
      throw new Error(errorMessage);
    }

    messageId = nanoid();
    const contentType = response.headers.get("Content-Type");

    if (contentType.startsWith("text/event-stream")) {
      // We took this variable to show streaming response for scout workflow
      const accumulatedMessages = [];
      const parser = createParser((e) => {
        onParse(e, accumulatedMessages);
      });

      const reader = response.body.getReader();

      await push(reader, parser).finally(() => {
        isAlreadyTranslated = false;
        setUIState("TEXT");
        enableDisableMessageButton(true);
      });
    } else {
      const data = await response.json();
      let options = [];

      if (data.sessionState) {
        const {
          sessionState: {
            status,
            response = null,
            sources = [],
            options: responseOptions = [],
            intent,
          },
        } = data;

        options = responseOptions;
        handleResponse({ message: response, sources, options });
        if (options.length > 0) {
          setUIState("OPTIONS"); // disables typing
        } else {
          setUIState("TEXT"); // enables typing
        }

        if (status === "intentFulfilled") {
          isAlreadyTranslated = false;
          enableDisableMessageButton(true);
          if (
            intent?.name !== "hellointent" &&
            intent?.name !== "thank_you_intent"
          ) {
            if (allowScoutFeedback) {
              if (
                !intent?.name.toLowerCase().includes("fallback") &&
                !intent?.name.toLowerCase().includes("search")
              ) {
                handleResponse({ message: followUpMessage });
                isInFollowUpContext = true;
              }
            } else {
              handleResponse({ message: followUpMessage });
              isInFollowUpContext = true;
            }
          }
        } else if (status === "intentFailed") {
          handleResponse({ message: followUpMessage });
          isAlreadyTranslated = false;
          isInFollowUpContext = true;
          enableDisableMessageButton(true);
        } else {
          enableDisableMessageButton(false);
        }
      } else if (data.message) {
        if (data?.isValid !== undefined && data.isValid === false) {
          handleResponse({ message: data.message });
        } else {
          const translatedMessage = await translateText(
            data.message,
            userLanguage
          );
          handleResponse({ message: translatedMessage });
          isAlreadyTranslated = false;
        }
        setUIState("TEXT", data.isValid !== false);
      } else {
        handleResponse({
          message:
            data.message || "Something went wrong. Please try again later.",
        });
      }
    }
  } catch (err) {
    console.error("Fetch Error:", err);
    setUIState("TEXT");
  }
}
const removeMessageButton = () => {
  const messageButton = document.querySelector(".custom-message-button");
  if (messageButton) {
    messageButton.remove();
  }
};

const handleNewUserMessage = async (message, phoneNumber) => {
  if (!message) {
    return;
  }
  if (!isMessageButtonEnabled && isMessageButtonEnabledOnFailure) {
    removeMessageButton();
  }
  const isNoResponse =
    typeof message === "string" ? message.toLowerCase() === "no" : "";
  if (
    !isAlreadyTranslated &&
    isMultiligualEnabled &&
    !(isInFollowUpContext && isNoResponse)
  ) {
    handleFollowUpMessageTranslation(message);
  }

  isInFollowUpContext = false;

  setUIState("LOADING");
  let requestOptions = {
    method: "POST",
    headers: {
      aud: "webchat",
      Authorization: `Bearer ${WEBCHAT_TOKEN}`,
      "Content-Type": "application/json",
    },
  };

  if (message instanceof File) {
    const formData = new FormData();
    formData.append("files", message);
    formData.append("langCode", userLanguage);
    formData.append("accountNumber", phoneNumber);
    delete requestOptions.headers["Content-Type"];
    requestOptions = {
      ...requestOptions,
      data: formData,
    };
    try {
      const fileResponse = await axios(
        `${ENGINE_URL}/photo-upload`,
        requestOptions
      );
      if (fileResponse.data.response) {
        isAlreadyTranslated = true;
        handleResponse({ message: fileResponse.data.response });
        setUIState("OPTIONS");
      }
      if (fileResponse.data.fileURL) {
        const fileURL = fileResponse.data.fileURL;
        requestOptions.headers["Content-Type"] = "application/json";
        requestOptions = {
          ...requestOptions,
          body: JSON.stringify({
            to: phoneNumber,
            from: uuid,
            body: fileURL,
            NumMedia: 1,
          }),
        };
        await handleLexRequest(requestOptions);
      } else {
        setUIState("TEXT");
      }
    } catch (error) {
      const contentType = error.response?.headers["content-type"];
      let errorMessage = "Something went wrong. Please try again later.";
      if (contentType && contentType.includes("application/json")) {
        errorMessage = error.response?.data?.message || errorMessage;

        // Handle message button logic on file upload failre
        const shouldShowMessageButton =
          isMessageButtonEnabled || isMessageButtonEnabledOnFailure;
        if (shouldShowMessageButton) {
          const timer = setTimeout(function () {
            addMessageComponent(timer, true, "onBotError");
          }, 1000);
        }
        handleResponse({ message: errorMessage });
      } else {
        handleResponse({ message: errorMessage });
      }

      console.error("Axios Error:", error);
      setUIState("TEXT");
    }
  } else {
    requestOptions = {
      ...requestOptions,
      body: JSON.stringify({
        to: phoneNumber,
        from: uuid,
        body: message,
      }),
    };
    // Engine Lex request
    await handleLexRequest(requestOptions);
  }
};

function push(reader, parser) {
  return new Promise((resolve, reject) => {
    reader
      .read()
      .then(({ done, value }) => {
        if (done) {
          resolve(); // Resolve the promise when done
          return;
        }
        const decodedValue = new TextDecoder().decode(value);
        parser.feed(decodedValue);
        push(reader, parser).then(resolve).catch(reject); // Recursively call push and resolve/reject based on the recursive calls
      })
      .catch(reject); // Reject the promise on error
  });
}

const handleQuickButtonClicked = (button, setDisableButtons) => {
  addUserMessage(button.charAt(0).toUpperCase() + button.slice(1));
  handleNewUserMessage(button, phoneNumber);
  setDisableButtons(true);
};

const LinkPreview = ({ link }) => {
  const { title: rawLink, url, metadata } = link;
  let title = rawLink;
  let URL = url;
  if (metadata) {
    title = metadata.title?.trim() || metadata.url;
    URL = metadata.url;
  }
  // Fix URLs with unbalanced parentheses (more opening than closing)
  if (URL) {
    const openParens = (URL.match(/\(/g) || []).length;
    const closeParens = (URL.match(/\)/g) || []).length;
    if (openParens > closeParens) {
      // Add missing closing parentheses
      URL = URL + ")".repeat(openParens - closeParens);
    }
  }
  // Fallback: if no title, use the URL as the display title
  if (!title && URL) {
    try {
      // Try to extract a readable title from the URL
      const urlObj = new window.URL(URL);
      // Use the last path segment as title, replacing hyphens with spaces
      const pathSegments = urlObj.pathname.split("/").filter(Boolean);
      const lastSegment =
        pathSegments[pathSegments.length - 1] || urlObj.hostname;
      title = decodeURIComponent(lastSegment)
        .replace(/-/g, " ")
        .replace(/\.(html?|php|aspx?)$/i, "")
        .replace(/^\w/, (c) => c.toUpperCase());
    } catch {
      // If URL parsing fails, just use the raw URL
      title = URL;
    }
  }

  if (!title) {
    return null;
  }
  const finalURL = useUtmUrl(URL, allowUTMLink, utmSource);
  return (
    <div
      className="link_review"
      id={URL}
      role="link"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          window.open(finalURL, "_blank", "noreferrer");
        }
      }}
      onClick={() => window.open(finalURL, "_blank", "noreferrer")}
    >
      <article className="article">
        <a
          href={finalURL}
          target="_blank"
          rel="noreferrer"
          aria-label={`Open link: ${title}`}
          tabIndex={-1}
        >
          <section className="article__content">
            <div className="article__description">
              <h3 className="article__title" title={title}>
                {title}
              </h3>
            </div>
          </section>
        </a>
      </article>
    </div>
  );
};

const SpeakButton = ({ isSpeaking, handleToggleSpeak, message }) => {
  return (
    <button
      className="speak__icon"
      onClick={() => handleToggleSpeak(message)}
      aria-label={isSpeaking ? "Mute speech output" : "Read message aloud"}
    >
      {isSpeaking ? (
        <img
          src={SPEAKER}
          alt=""
          width="20px"
          height="20px"
          aria-hidden="true"
        />
      ) : (
        <img
          src={SPEAKER_MUTE}
          alt=""
          width="20px"
          height="20px"
          aria-hidden="true"
        />
      )}
    </button>
  );
};

const SpeakOptionsButton = ({
  isSpeakingOption,
  handleToggleSpeak,
  message,
}) => {
  const ariaLabel = isSpeakingOption
    ? `Stop reading option: ${message}`
    : `Read aloud option: ${message}`;

  return (
    <button
      className="rcw_speak__icon"
      onClick={() => handleToggleSpeak(message)}
      aria-label={ariaLabel}
    >
      {isSpeakingOption ? (
        <img src={SPEAKER} alt="speaker playing" width="20px" height="20px" />
      ) : (
        <img
          src={SPEAKER_MUTE}
          alt="speaker muted"
          width="20px"
          height="20px"
        />
      )}
    </button>
  );
};

const CustomResponseMessageComponent = React.memo(
  ({ message, links, buttons, messageId }) => {
    const [disableButtons, setDisableButtons] = useState(false);
    const [selectedButtonIndex, setSelectedButtonIndex] = useState(null);
    const [isSpeaking, setIsSpeaking] = useState(false);
    const [isSpeakingOptions, setIsSpeakingOptions] = useState([]);
    const utteranceRef = useRef(null);
    const showLinks = !hideLinkPreviewCard;

    const formatTime = () => {
      return parse(
        moment(parseInt(Date.now()))
          .format("hh:mm A")
          .replace(/\b(st|nd|rd|th)\b/g, "<sup>$2</sup>")
      );
    };

    const handleSpeak = useCallback((message, type, buttonIndex) => {
      const parser = new DOMParser();
      const doc = parser.parseFromString(message, "text/html");
      const text = doc.body.textContent;
      const speech = new SpeechSynthesisUtterance(text);
      // const langCode = franc(text);
      const langCode = userLanguage;
      const lang = languageMap[langCode] || "en-US"; // Default to English if language is not recognized
      speech.lang = lang;
      speech.rate = 0.8;
      window.speechSynthesis.cancel();
      speech.onend = () => handleStop(buttonIndex);
      window.speechSynthesis.speak(speech);
      utteranceRef.current = speech;
      if (type === "option") {
        setIsSpeakingOptions((prev) => {
          const newState = [...prev];
          newState[buttonIndex] = true;
          return newState;
        });
      } else {
        setIsSpeaking(true);
      }
    }, []);

    const handleStop = useCallback((buttonIndex) => {
      window.speechSynthesis.cancel();
      if (utteranceRef.current) {
        utteranceRef.current.onend = null;
      }
      setIsSpeaking(false);
      setIsSpeakingOptions((prev) => {
        const newState = [...prev];
        if (buttonIndex !== undefined) {
          newState[buttonIndex] = false;
        } else {
          newState.fill(false);
        }
        return newState;
      });
    }, []);

    const handleToggleSpeak = useCallback(
      (message) => {
        if (isSpeaking) {
          handleStop();
        } else {
          handleSpeak(message, "message");
        }
      },
      [isSpeaking, handleSpeak, handleStop]
    );

    const handleToggleSpeakOption = useCallback(
      (message, buttonIndex) => {
        if (isSpeakingOptions[buttonIndex]) {
          handleStop(buttonIndex);
        } else {
          handleSpeak(message, "option", buttonIndex);
        }
      },
      [isSpeakingOptions, handleSpeak, handleStop]
    );

    useEffect(() => {
      return () => {
        handleStop();
      };
    }, [handleStop]);
    return (
      <div className="container" key={messageId}>
        <div className="rcw-response">
          <div className="rcw-message-text" id={messageId}>
            {message &&
              parse(convertTextToHtml(message, { target: "_blank" }, true))}
            {(() => {
              return null;
            })()}
            {showLinks &&
              links?.map((link, index) => {
                return <LinkPreview key={index} link={link} />;
              })}
            <SpeakButton
              isSpeaking={isSpeaking}
              handleToggleSpeak={handleToggleSpeak}
              message={message}
            />
          </div>
          {buttons?.length > 0 && (
            <div className="quick-buttons-container rcw-quick-buttons-container">
              <ul className="quick-buttons rcw-quick-buttons-ul">
                {buttons.map((button, buttonIndex) => (
                  <li
                    key={buttonIndex}
                    className="quick-list-button rcw-quick-button-li"
                  >
                    <button
                      className={`quick-button rcw-quick-button-btn ${
                        disableButtons && selectedButtonIndex !== buttonIndex
                          ? "rcw-quick-button-not-selected"
                          : `${
                              selectedButtonIndex === buttonIndex
                                ? "rcw-quick-button-selected"
                                : ""
                            }`
                      }`}
                      disabled={disableButtons}
                      tabIndex={disableButtons ? -1 : 0}
                      onClick={() => {
                        handleQuickButtonClicked(button, setDisableButtons);
                        setSelectedButtonIndex(buttonIndex);
                      }}
                      aria-label={`Send quick reply: ${button}`}
                    >
                      <span className="rcw-quick-button-text">{button}</span>
                    </button>
                    <SpeakOptionsButton
                      isSpeakingOption={isSpeakingOptions[buttonIndex]}
                      handleToggleSpeak={() =>
                        handleToggleSpeakOption(button, buttonIndex)
                      }
                      message={button}
                    />
                  </li>
                ))}
              </ul>
            </div>
          )}
          <span className="rcw-timestamp">Received {formatTime()}</span>
        </div>
      </div>
    );
  }
);

const addCustomMessage = () => {
  addUserMessage("Message");
  handleNewUserMessage("Message", phoneNumber);
};

const MessageButtonComponent = ({ enableMessageButton, type }) => {
  return (
    <div className="custom-message-button">
      {enableMessageButton === true && <button>Message</button>}
      {type === "onBotLoad" && !isPoweredByHidden && (
        <a
          className="powered-by"
          href={CITIBOT_LANDING_PAGE_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          <span className="p-span">Powered by</span>
          <span className="c-span">CITIBOT</span>
        </a>
      )}
    </div>
  );
};

const enableDisableMessageButton = (toggleState) => {
  const messageButton = document.querySelector(".custom-message-button button");
  if (messageButton) {
    if (toggleState) {
      messageButton.disabled = false;
      messageButton.tabIndex = 0;
      messageButton.ariaLabel = "Send custom message";
      messageButton.ariaDisabled = false;
      messageButton.style.backgroundColor = messageButtonColor;
      messageButton.style.color = messageTextColor;
      messageButton.addEventListener("click", addCustomMessage);
      messageButton.classList.add("hover-effect");
    } else {
      messageButton.disabled = true;
      messageButton.ariaDisabled = true;
      messageButton.ariaLabel = "Send custom message (disabled)";
      messageButton.tabIndex = -1;
      messageButton.style.backgroundColor = "grey";
      messageButton.style.color = "white";
      messageButton.removeEventListener("click", addCustomMessage);
      messageButton.classList.remove("hover-effect");
      // Prevent current focus
      if (document.activeElement === messageButton) {
        messageButton.blur();
      }
    }
  }
};

function setSendButtonEnabled(enabled) {
  const svgDisabled = document.querySelector(".rcw-send-icon-disabled");
  const svgEnabled = document.querySelector(".rcw-send-icon");

  // If we want ENABLED state
  if (enabled) {
    const svg = svgDisabled || svgEnabled;
    if (svg) {
      if (svg.querySelector("polygon")) {
        svg.querySelector("polygon").style.fill = messageButtonColor;
      }
      svg.setAttribute("class", "rcw-send-icon");
      const button = svg.closest("button");
      if (button) {
        button.ariaDisabled = false;
        button.disabled = false;
        button.tabIndex = 0;
        button.ariaLabel = "Send message";
      }
    }
    return;
  }

  // If we want DISABLED state
  const svg = svgEnabled || svgDisabled;
  if (svg) {
    if (svg.querySelector("polygon")) {
      svg.querySelector("polygon").style.fill = "grey";
    }
    svg.setAttribute("class", "rcw-send-icon-disabled");
    const button = svg.closest("button");
    if (button) {
      button.ariaDisabled = true;
      button.disabled = true;
      button.tabIndex = -1;
      button.ariaLabel = "Send message (disabled)";
    }
  }
}

const addMessageComponent = (
  timer,
  enableMessageButton,
  type = "onBotLoad"
) => {
  const sendDiv = document.querySelector(".rcw-sender");
  const messageButton = document.querySelector(".custom-message-button button");
  const existingMessageButtonDiv = document.querySelector(
    ".custom-message-button"
  );
  // Prevent duplicates remove if already exists
  if (existingMessageButtonDiv) {
    existingMessageButtonDiv.remove();
  }
  if (sendDiv && !messageButton) {
    // Element has loaded
    sendDiv.insertAdjacentHTML(
      "afterend",
      ReactDOMServer.renderToStaticMarkup(
        <MessageButtonComponent
          enableMessageButton={enableMessageButton}
          type={type}
        />
      )
    );
    clearTimeout(timer);
    if (enableMessageButton) {
      enableDisableMessageButton(true);
    }
  }
};

function App(props) {
  const [isChatOpen, setIsChatOpen] = useState(false); // Track chatbot open status
  const [isWelcomeMessageSet, setIsWelcomeMessageSet] = useState(false);
  const [titleAvatar, setTitleAvatar] = useState("");
  const [title, setTitle] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [customerLogo, setCustomerLogo] = useState("");
  const [launcherOpenImg, setLauncherOpenImg] = useState("");
  const [launcherCloseImg, setLauncherCloseImg] = useState("");
  const [launcherImgLoaded, setLauncherImgLoaded] = useState(false);
  const [enableMessageButton, setEnableMessageButton] = useState(false);
  const [enableHideChatbotButton, setEnableHideChatbotButton] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showChatbot, setShowChatbot] = useState(true);

  const searchParams = new URLSearchParams(window.location.search);
  let accountId = searchParams.get("accountId");
  if (!accountId) {
    accountId = props.accountId;
  }

  useEffect(() => {
    fetch(`${API_URL}/account/${accountId}/chatConfig`, {
      headers: {
        "Content-Type": "application/json",
        "x-audience": "budibase",
        Authorization: `Bearer ${BUDIBASE_TOKEN}`,
      },
    })
      .then((response) => {
        // Handle 204 No Content response
        if (response.status === 204) {
          console.warn("No content returned from the server.");
          return {}; // Return an empty object or handle as needed
        }
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        return response.json();
      })
      .then((data) => {
        phoneNumber = data._id;
        isMessageButtonEnabled = data.enableMessageButton ?? false;
        isMessageButtonEnabledOnFailure =
          data.enableMessageButtonOnFailure ?? false;
        isMultiligualEnabled = data.multilingual ?? false;
        isPoweredByHidden = data.isPoweredByHidden ?? false;
        setLauncherOpenImg(data.chatOpenningImgUrl);
        setLauncherCloseImg(data.chatCloseImgUrl);
        setTitleAvatar(data.botProfileImgUrl);
        setTitle(data.chatWidgetTitle);
        setSubtitle(data.chatWidgetSubTitle);
        setCustomerLogo(data.logoImgUrl);
        setEnableMessageButton(data.enableMessageButton);
        setEnableHideChatbotButton(data.enableHideChatbotButton);
        setLauncherImgLoaded(true);
        isPhotoUpload = data.isPhotoUpload ?? false;
        setLoading(false);
        allowScoutFeedback = data.allowScoutFeedback ?? false;
        scoutWorkflowId = data.scoutWorkflowId ?? "";
        allowUTMLink = data.allowUTMLink ?? false;
        utmSource = data.utmSource ?? "";
        hideLinkPreviewCard = data.hideLinkPreviewCard ?? false;
        document.documentElement.style.setProperty(
          "--header-background-color",
          data.headerBackgroundColor
        );
        document.documentElement.style.setProperty(
          "--header-text-color",
          data.headerTextColor
        );
        document.documentElement.style.setProperty(
          "--message-background-color",
          data.messageBackgroundColor
        );
        document.documentElement.style.setProperty(
          "--message-text-color",
          data.messageTextColor
        );

        messageButtonColor = data.headerBackgroundColor;
        messageTextColor = data.headerTextColor;

        if (!isWelcomeMessageSet) {
          renderCustomComponent(
            () => (
              <CustomResponseMessageComponent message={data.greetingCopy} />
            ),
            [],
            true
          );
          setIsWelcomeMessageSet(true);
        }
      })
      .catch((error) => {
        console.error("Error:", error);
        setLoading(false);
        renderCustomComponent(
          () => (
            <CustomResponseMessageComponent message="Sorry, something went wrong. Please try again later." />
          ),
          [],
          true
        );
      });
  }, [isChatOpen, isWelcomeMessageSet, accountId, loading, isPhotoUpload]);

  useEffect(() => {
    if (window.parent !== window) {
      window.parent.postMessage(
        {
          type: isChatOpen ? "CHATBOT_OPENED" : "CHATBOT_CLOSED",
        },
        "*"
      );
    }
  }, [isChatOpen]);

  // When the widget library closes itself via Escape, it does NOT call handleToggle,
  // so isChatOpen stays true and the focus-return useEffect never fires.
  // Listening for Escape here keeps isChatOpen in sync with the widget's internal state.
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && isChatOpen) {
        setIsChatOpen(false);
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isChatOpen]);

  const wasChatOpenRef = useRef(false);
  useEffect(() => {
    if (isChatOpen) {
      wasChatOpenRef.current = true;
      const hideBtn = document.querySelector(".hide-chatbot-button");
      hideBtn?.focus();
    } else if (wasChatOpenRef.current) {
      // Return focus to launcher when chat closes so focus doesn't escape the iframe
      const launcherBtn = document.querySelector(".launcherBtn");
      launcherBtn?.focus();
    }
  }, [isChatOpen]);

  const handlePaste = (event) => {
    event.preventDefault();
    const text = (event.clipboardData || window.clipboardData).getData("text");
    document.execCommand("insertText", false, text);
  };

  const waitForInputAndBindPaste = (retries = 10) => {
    // Retry Until Input Appears
    const contentEditableDiv = document.querySelector(".rcw-input");
    if (contentEditableDiv) {
      contentEditableDiv.removeEventListener("paste", handlePaste);
      contentEditableDiv.addEventListener("paste", handlePaste);
    } else if (retries > 0) {
      setTimeout(() => waitForInputAndBindPaste(retries - 1), 300);
    } else {
      console.warn("Failed to bind paste handler — input not found.");
    }
  };

  const handleToggle = (toggleState) => {
    // If toggleState is an event object, toggle the current state
    const newState =
      typeof toggleState === "boolean" ? toggleState : !isChatOpen;

    setIsChatOpen(newState);
    launcherImg = newState ? launcherCloseImg : launcherOpenImg;

    if (newState) {
      const timer = setTimeout(function () {
        addMessageComponent(timer, enableMessageButton);
      }, 1000);
      waitForInputAndBindPaste();
    }
  };

  const getCustomLauncher = (handleToggle) => {
    return (
      <>
        {launcherImgLoaded ? (
          <div
            className={`launcher-container ${
              !isChatOpen ? "chat-closed" : "chat-open"
            }`}
          >
            <button
              className="launcherBtn"
              onClick={handleToggle}
              aria-label={isChatOpen ? "Close chatbot" : "Open chatbot"}
              aria-expanded={isChatOpen}
              aria-labelledby="chat-launcher-label"
            >
              <img
                className="launcherImg"
                src={launcherImg || launcherOpenImg}
                alt=""
                aria-hidden="true"
              />
              <span id="chat-launcher-label" className="sr-only">
                Let's chat
              </span>
            </button>

            {!isChatOpen && enableHideChatbotButton && (
              <div
                className="hide-chatbot-container"
                role="group"
                aria-label="Chat controls"
              >
                <button
                  className="hide-chatbot-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowChatbot(false);
                    setIsChatOpen(false);
                    if (window.parent !== window) {
                      window.parent.postMessage(
                        { type: "HIDE_CHATBOT_IFRAME" },
                        "*"
                      );
                    }
                  }}
                  aria-label="Hide chatbot"
                  aria-controls="citibot_support_chat"
                >
                  <svg
                    width="19px"
                    height="19px"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="hide-chatbot-svg"
                  >
                    <path
                      d="M18 6L6 18M6 6L18 18"
                      style={{
                        stroke: "var(--header-background-color)",
                        strokeWidth: 3,
                      }}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>
            )}
          </div>
        ) : null}
      </>
    );
  };

  return (
    <div className="App">
      {showChatbot && (
        <Widget
          handleNewUserMessage={(message) =>
            handleNewUserMessage(message, phoneNumber)
          }
          profileAvatar={customerLogo || logo}
          profileClientAvatar={USER_AVATAR}
          title={title || "Citibot"}
          subtitle={subtitle || ""}
          titleAvatar={titleAvatar}
          resizable={false}
          handleToggle={handleToggle}
          launcher={(handleToggle) => getCustomLauncher(handleToggle)}
          photoUploadIcon={isPhotoUpload}
          messageButtonColor={messageButtonColor}
          acceptedImageTypes={ALLOWED_IMAGE_TYPES}
          isOpen={isChatOpen}
        />
      )}
    </div>
  );
}

export default App;
